import { isPublicLawProcedureType } from '../case-wiki/profile.js';

/**
 * Convention de numérotation des pièces selon l'ordre de juridiction du dossier.
 *
 * - `civil` : usage professionnel « Pièce n°1 - Carte d'identité ». Aucun texte
 *   ne l'impose, c'est la coutume du bordereau de communication (art. 768 CPC).
 * - `administratif` : « 1 Carte d'identité ». Imposé par le CJA : R. 412-2
 *   (inventaire, numéro d'ordre continu et croissant) et, depuis le 1er juin
 *   2021 (décret 2020-1245), R. 414-5 / R. 611-8-5 : chaque fichier déposé par
 *   Télérecours porte un intitulé « commençant par le numéro d'ordre ». Un nom
 *   « Pièce n°1 … » ne commence pas par le numéro : la pièce peut être écartée.
 *   Mêmes règles devant la CAA et le Conseil d'État (R. 811-13, R. 821-6), d'où
 *   un déclencheur par matière et non par juridiction.
 *
 * Source : https://www.legifrance.gouv.fr/codes/article_lc/LEGIARTI000042416950 (verified 2026-09-22)
 * Issue : hector-ai-legal/hector-monorepo#1704
 */
export type PieceNamingConvention = 'civil' | 'administratif';

/**
 * Déduit la convention de la matière du dossier (`cases.procedure_type`).
 * Tout dossier de droit public (TA, CAA, CE) suit la convention administrative ;
 * une matière absente ou inconnue retombe sur le civil, comportement historique.
 */
export function resolvePieceNamingConvention(
  procedureType: string | null | undefined,
): PieceNamingConvention {
  return isPublicLawProcedureType(procedureType) ? 'administratif' : 'civil';
}

// Préfixe déjà écrit dans un libellé : « Pièce n°3 - », « Pièce N° 3 : »,
// « Pce 3 », « Prod. n°3 », « Production 3 ». Le numéro est capturé pour ne
// retirer le préfixe que s'il porte le MÊME numéro que la pièce.
const MARKED_PREFIX_RE =
  /^\s*(?:pi[eè]ce|pce|prod\.?|production)\s*(?:n\s*[°ºo]\.?|num[ée]ro|#)?\s*(\d+[a-z]*(?:[.-]\d+)*)\s*(?:[:.\-–—)_]\s*|\s+|$)/iu;
// Numéro nu en tête : « 3 », « 3. », « 003 - », « 3) ».
// Le souligné compte : « 003_Arrete » est la forme Télérecours « 001_libellé ».
const BARE_PREFIX_RE = /^\s*(\d+[a-z]*(?:[.-]\d+)*)\s*(?:[:.\-–—)_]\s*|\s+|$)/iu;

function canonicalNumber(value: string): string {
  return value.trim().toLowerCase().replace(/^0+(?=\d)/, '');
}

/**
 * Retire d'un libellé le préfixe de numérotation qui répète le numéro de la
 * pièce, pour éviter « 3 3 Arrêté » ou « 3 Pièce n°3 - Arrêté » quand le
 * numéro a été saisi à la fois dans le libellé et dans `piece_number`.
 * Un numéro DIFFÉRENT est conservé : « 12 mars 2024 courrier » reste intact
 * pour la pièce 3, car ce « 12 » appartient au libellé.
 */
export function stripLeadingPieceMarker(label: string, pieceNumber: string): string {
  const expected = canonicalNumber(pieceNumber);
  for (const re of [MARKED_PREFIX_RE, BARE_PREFIX_RE]) {
    const match = re.exec(label);
    if (match && canonicalNumber(match[1] ?? '') === expected) {
      return label.slice(match[0].length).trim();
    }
  }
  return label.trim();
}

/**
 * Libellé administratif affiché : `1 Carte d'identité`. Accents conservés,
 * pas de « Pièce n° », pas de point après le numéro, pas de zéro de tête.
 */
export function formatAdministrativePieceLabel(pieceNumber: string, label: string): string {
  const number = pieceNumber.trim();
  const rest = stripLeadingPieceMarker(label, number);
  return rest ? `${number} ${rest}` : number;
}

// Contraintes Télérecours sur les noms de fichiers :
// - 80 caractères maximum, extension comprise ;
// - accents remplacés par la lettre sans accent ;
// - pas de caractère spécial (%, /, &…), pas de point hors extension ;
// - tiret et souligné acceptés ; l'espace et l'apostrophe figurent dans les
//   exemples officiels (« 001 Avis d'imposition 2020 », « 1 Avis d'imposition
//   2020 »), donc on les garde plutôt que de tout souligner.
// Source: https://www.telerecours.fr/documentation-telerecours/gerer-des-dossiers/deposer-une-requete/deposer-requete-telerecours/ (verified 2026-06-20)
// Source: https://www.telerecours.fr/documentation-telerecours/gerer-des-dossiers/deposer-une-requete/deposer-requete-telerecours/TR-FicheMemo-Deposer-une-requete.pdf (verified 2026-09-22)
export const TELERECOURS_FILENAME_MAX_LENGTH = 80;
const TELERECOURS_DISALLOWED_RE = /[^A-Za-z0-9 '_-]/g;
const EDGE_SEPARATORS_RE = /^[\s'_-]+|[\s'_-]+$/g;

/**
 * Translittère une chaîne dans l'alphabet accepté par Télérecours : lettres
 * sans accent, chiffres, espace, apostrophe droite, tiret, souligné. Tout
 * autre caractère (dont le point) devient une espace, puis les espaces
 * multiples sont réduites. Idempotente.
 */
export function toTelerecoursSafeText(input: string): string {
  if (!input) return '';
  const preNormalized = input
    // NFD ne décompose pas les ligatures : sans ces remplacements « œuvre »
    // perdrait sa première lettre.
    .replace(/œ/g, 'oe')
    .replace(/Œ/g, 'OE')
    .replace(/æ/g, 'ae')
    .replace(/Æ/g, 'AE')
    .replace(/ß/g, 'ss')
    .replace(/[’‘ʼ]/g, "'");
  return preNormalized
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(TELERECOURS_DISALLOWED_RE, ' ')
    .replace(/\s+/g, ' ')
    .replace(EDGE_SEPARATORS_RE, '');
}

export interface AdministrativePieceFileNameInput {
  /** Valeur de `case_documents.piece_number` (`'1'`, `'5bis'`, `'2.3'`). */
  pieceNumber: string;
  /** Libellé de la pièce, sans extension. */
  baseName: string;
  /** Extension avec ou sans point (`'pdf'`, `'.pdf'`), vide si aucune. */
  extension?: string;
}

/**
 * Nom de fichier administratif conforme Télérecours : `1 Carte d'identite.pdf`.
 * Le numéro ouvre le nom (R. 414-5 CJA). Seul le libellé est tronqué pour
 * tenir en 80 caractères, jamais le numéro ni l'extension : ce sont eux qui
 * rattachent le fichier à sa ligne d'inventaire.
 */
export function formatAdministrativePieceFileName(input: AdministrativePieceFileNameInput): string {
  const rawNumber = input.pieceNumber.trim();
  if (!rawNumber) {
    throw new RangeError(
      `formatAdministrativePieceFileName: pieceNumber is empty for "${input.baseName}". `
        + 'Only numbered pieces get a Télérecours name; callers must keep the original file name for unnumbered pieces.',
    );
  }
  // Un point interne est interdit : une sous-numérotation « 2.3 » devient « 2-3 ».
  const number = toTelerecoursSafeText(rawNumber.replace(/\./g, '-')).replace(/\s+/g, '');
  const rawExtension = (input.extension ?? '').trim().replace(/^\./, '').toLowerCase();
  const extension = rawExtension.replace(/[^a-z0-9]/g, '');
  const suffix = extension ? `.${extension}` : '';
  const base = toTelerecoursSafeText(stripLeadingPieceMarker(input.baseName, rawNumber));
  const available = TELERECOURS_FILENAME_MAX_LENGTH - number.length - suffix.length - 1;
  const truncated = base.length > available
    ? base.slice(0, Math.max(available, 0)).replace(EDGE_SEPARATORS_RE, '')
    : base;
  return truncated ? `${number} ${truncated}${suffix}` : `${number}${suffix}`;
}

// Titre d'une liste de pièces, une fois ôtés dièses, gras et espaces. Sensible à la casse comme
// le détecteur de l'add-in (`markdownParser.ts`) : une phrase « le bordereau de … » n'est pas un
// titre. « INVENTAIRE » seul ne suffit pas : un titre « INVENTAIRE DES BIENS » d'un acte de droit
// public n'est pas une liste de pièces. Deux formes ajoutées par #1786, relevées dans les prompts
// en base : un numéro de section devant le titre (« V. INVENTAIRE DES PIÈCES COMMUNIQUÉES », requête
// fiscale) et les intitulés des actes indemnitaires (« PIÈCES ANNEXÉES », « PIÈCES JOINTES »).
//
// Seuls des intitulés de LISTE explicites : « BORDEREAU » seul, « PIÈCES PRODUITES » ou « PIÈCES
// COMMUNIQUÉES » ne déclenchent pas. Revue de #1791 (2026-09-29) : « ## II. PIÈCES PRODUITES PAR LE
// REQUÉRANT » est un titre de DISCUSSION, et le retrait emportait l'argumentation qui le suivait.
const PIECE_LIST_HEADING_RE =
  /^(?:(?:[IVXLC]+|\d+)[.)]\s+)?(?:BORDEREAU\s+(?:DE\s+COMMUNICATION\s+)?D(?:E|ES)\s+PI[EÈ]CES|LISTE\s+DES\s+PI[EÈ]CES|INVENTAIRE(?:\s+D[ÉE]TAILL[ÉE])?\s+DES\s+PI[EÈ]CES|PI[EÈ]CES\s+(?:JOINTES|ANNEX[ÉE]ES))\b/u;
const MARKDOWN_HEADING_RE = /^\s*#{1,6}\s/;

function stripHeadingMarkup(line: string): string {
  return line
    .trim()
    .replace(/^#{1,6}\s*/, '')
    .replace(/^\*\*|\*\*$/g, '')
    .replace(/^__|__$/g, '')
    .replace(/:$/, '')
    .trim();
}

/**
 * Vrai pour une ligne titre de liste de pièces : « BORDEREAU DE PIÈCES »,
 * « ## BORDEREAU DES PIÈCES COMMUNIQUÉES », « **LISTE DES PIÈCES** »,
 * « INVENTAIRE DES PIÈCES », « ## V. INVENTAIRE DES PIÈCES COMMUNIQUÉES », « ## PIÈCES ANNEXÉES ».
 *
 * Volontairement plus étroit que `isBordereauLine` (`src/redaction/marqueurs.ts`),
 * qui sert au STYLE et tolère un « BORDEREAU » en milieu de ligne : ici la
 * ligne déclenche un RETRAIT, elle doit donc être ancrée en tête et courte.
 */
function isPieceListHeadingLine(line: string): boolean {
  const text = stripHeadingMarkup(line);
  if (text.length === 0 || text.length > 80 || !PIECE_LIST_HEADING_RE.test(text)) return false;
  // Hors titre markdown, exiger la ligne ENTIÈREMENT en capitales — la même garde de casse
  // qu'`estBordereauAncre` (`redaction/marqueurs.ts`), pour la même raison : un vrai titre de
  // liste est toujours en capitales.
  //
  // Mesuré le 2026-09-23 : recopiée telle quelle par le modèle, une consigne de prompt
  // (« INVENTAIRE DES PIÈCES : Liste exhaustive et numérotée ») passait pour un titre de liste.
  return MARKDOWN_HEADING_RE.test(line) || text === text.toUpperCase();
}

// Préfixe de pièce facultatif d'une ligne d'inventaire : « Pièce n° », « Prod. n° »…
const INVENTORY_PIECE_PREFIX_RE = /^(?:pi[eè]ce|pce|prod\.?|production)\s*(?:n\s*[°ºo]\.?|num[ée]ro|#)?\s*/iu;
// Numéro de pièce : « 3 », « 2.3 », « 1-2 » (sous-numéro, cf. @hector/piece-number-sort),
// « 5bis », « 5 bis ». Le lookahead refuse « 1er », « 3 terrain » lu comme « 3ter ».
const INVENTORY_PIECE_NUMBER_RE = /^\d+(?:[.-]\d+)*(?:\s?(?:bis|ter|quater)(?!\p{L})|[a-z](?!\p{L}))?/iu;
const AFTER_NUMBER_SEPARATOR_RE = /^\s*(?:[:.\-–—)_]\s*|\s+|$)/u;
// « 1. Pièce n°3 : … » : l'index d'une liste ordonnée n'est pas le numéro de la
// pièce ; on le retire quand un préfixe de pièce le suit.
const ORDERED_INDEX_BEFORE_PREFIX_RE =
  /^\d+[.)]\s+(?=(?:\*\*|__)?\s*(?:pi[eè]ce|pce|prod\.?|production)(?!\p{L}))/iu;

function parsePieceText(text: string): { number: string; label: string } | null {
  const rest = text.replace(INVENTORY_PIECE_PREFIX_RE, '');
  const number = INVENTORY_PIECE_NUMBER_RE.exec(rest);
  if (!number) return null;
  const afterNumber = rest.slice(number[0].length);
  const separator = AFTER_NUMBER_SEPARATOR_RE.exec(afterNumber);
  if (!separator) return null;
  return { number: number[0].replace(/\s+/g, ''), label: afterNumber.slice(separator[0].length).trim() };
}

function isPieceText(text: string): boolean {
  return parsePieceText(text.replace(ORDERED_INDEX_BEFORE_PREFIX_RE, '')) !== null;
}

/**
 * Vrai pour une ligne qui ferme un inventaire : un titre markdown, ou un
 * intitulé qui n'est pas une pièce — ligne entièrement en gras
 * (« **ANNEXE : JURISPRUDENCES CITÉES** ») ou entièrement en capitales
 * (« SOUS TOUTES RÉSERVES »). Sert au RENDU (Word, Canvas) des actes qui portent
 * encore un « INVENTAIRE DES PIÈCES » — ceux générés avant #1786 —, pour que
 * les lignes numérotées d'une annexe qui suit ne prennent pas le style de pièce.
 */
export function endsAdministrativeInventory(line: string): boolean {
  if (MARKDOWN_HEADING_RE.test(line)) return true;
  const text = line.trim();
  if (!text || /^[-*•]\s/u.test(text) || isPieceListHeadingLine(text)) return false;
  const unbolded = text.replace(/^(\*\*|__)([\s\S]+)\1:?$/u, '$2').trim();
  if (isPieceText(unbolded)) return false;
  if (unbolded !== text) return true;
  const letters = unbolded.replace(/[^\p{L}]/gu, '');
  return letters.length >= 3 && letters === letters.toUpperCase();
}

// Unités de traitement : une ligne, ou un paragraphe délimité par `[/PARA]`,
// `[/TITRE]`, `[/SECTION]` (et variantes `[/PARA:NOUVEAU]`, `>` malformé). Le
// markdown persisté d'un acte généré garde ces délimiteurs, et plusieurs
// paragraphes y tiennent parfois sur une même ligne.
const SEGMENT_SEPARATOR_RE = /(\r?\n|\[\/(?:PARA|TITRE|SECTION)[^\]>\n]{0,24}[\]>])/iu;

// Ligne de liste qui porte un préfixe de pièce, quel que soit l'identifiant qui suit : chiffres,
// mais aussi lettres (« Pièce n°A », pièces de l'administration défenderesse) et « Pièce n°D-1 »
// (pièces de la défense), que `parsePieceText` ne lit pas comme des numéros.
const PREFIXED_PIECE_ITEM_RE = /^(?:pi[eè]ce|pce|prod\.?|production)\s*(?:n\s*[°ºo]\.?|num[ée]ro|#)\s*[a-z\d]/iu;

// Mois en tête de libellé : « 12 mars 2024, le préfet… » est une date qui ouvre une phrase, pas
// la pièce n°12.
const LABEL_STARTS_WITH_MONTH_RE =
  /^(?:janvier|f[ée]vrier|mars|avril|mai|juin|juillet|ao[uû]t|septembre|octobre|novembre|d[ée]cembre)(?!\p{L})/iu;
// Auxiliaires et modaux conjugués : un libellé de pièce est un groupe nominal (« Arrêté du 12
// mars »), jamais une proposition (« La pièce n°3 est un faux »).
const LABEL_CONJUGATED_VERB_RE =
  /(?:^|\s)(?:est|sont|a|ont|était|étaient|avait|avaient|sera|seront|doit|doivent|peut|peuvent)(?=\s|$)/iu;
const INVENTORY_ITEM_LABEL_MAX_LENGTH = 150;

/**
 * Vrai pour une ligne « numéro nu + intitulé » d'une liste de pièces : « 1 Carte d'identité »,
 * « 4. Accusé de réception », « 2 - Arrêté du 12 mars 2024 ». Un paragraphe qui commence
 * seulement par un chiffre n'en est pas une : l'intitulé doit être court, ne pas finir comme une
 * phrase (« . », « ; »…), ne pas ouvrir sur un mois (une date) ni porter de verbe conjugué.
 */
function isBareInventoryItem(text: string): boolean {
  const parsed = parsePieceText(text);
  if (!parsed) return false;
  const label = parsed.label.replace(/(?:\*\*|__)\s*$/u, '').trim();
  return label.length > 0
    && label.length <= INVENTORY_ITEM_LABEL_MAX_LENGTH
    && !/[.;:!?,]$/u.test(label)
    && !LABEL_STARTS_WITH_MONTH_RE.test(label)
    && !LABEL_CONJUGATED_VERB_RE.test(label);
}

/**
 * Vrai pour une ligne de liste de pièces : « 1 Carte », « - Pièce n°1 : Carte »,
 * « **Pièce n°A** : Arrêté », « 1. Pièce n°3 : Avis », « Pièce n°D-1 : Mémoire ».
 *
 * Borne volontairement plus stricte que `endsAdministrativeInventory` : ici la ligne est
 * SUPPRIMÉE. Tout ce qui n'est pas reconnu comme une pièce ferme la liste et reste dans
 * l'acte — une formule « Fait à Paris, le … » ou un paragraphe « 12 mars 2024, le préfet a
 * statué. » placé après le titre ne doit jamais disparaître.
 */
function isInventoryItemSegment(segment: string): boolean {
  const text = segment
    .trim()
    .replace(/^[-*•]\s+/u, '')
    .replace(ORDERED_INDEX_BEFORE_PREFIX_RE, '')
    .replace(/^(?:\*\*|__)/u, '');
  return PREFIXED_PIECE_ITEM_RE.test(text) || isBareInventoryItem(text);
}

/** État du retrait transmis d'un fragment de flux au suivant. */
export interface AdministrativeInventoryStripState {
  /** Un titre de liste est passé, et aucune ligne ne l'a encore refermée. */
  insideInventory: boolean;
  /**
   * Le fragment s'est arrêté AU MILIEU d'une ligne retirée : le début du fragment suivant en
   * est la fin, et part avec elle. Cas du flush de sécurité de l'add-in, qui coupe le texte à
   * une espace quand le modèle marque une pause — « Pièce n°2 : » d'un côté, « Arrêté du 12
   * mars » de l'autre. Sans cet état, la seconde moitié entrerait seule dans Word.
   */
  midItem: boolean;
}

/** État d'un texte qui commence hors de toute liste. */
export const INITIAL_ADMINISTRATIVE_INVENTORY_STRIP_STATE: AdministrativeInventoryStripState = {
  insideInventory: false,
  midItem: false,
};

/**
 * Variante par fragment de `stripAdministrativeInventory`, pour un texte qui arrive en
 * morceaux : l'add-in Word écrit chaque paragraphe dès son délimiteur, sans jamais avoir
 * l'acte entier. L'état passe d'un fragment au suivant. Une seule grammaire pour le document
 * entier et pour le flux.
 */
export function stripAdministrativeInventoryFragment(
  fragment: string,
  state: AdministrativeInventoryStripState,
): { markdown: string; state: AdministrativeInventoryStripState } {
  if (!fragment) return { markdown: fragment, state };
  // `split` avec groupe capturant alterne contenu (indices pairs) et séparateur (impairs) ; le
  // dernier contenu n'a pas de séparateur : non vide, c'est une ligne coupée en cours.
  const parts = fragment.split(SEGMENT_SEPARATOR_RE);
  let inside = state.insideInventory;
  let midItem = false;
  let kept = '';
  for (let i = 0; i < parts.length; i += 2) {
    const segment = parts[i] ?? '';
    const separator = parts[i + 1] ?? '';
    const unterminated = i === parts.length - 1 && segment.trim().length > 0;
    const continuation = i === 0 && state.midItem && inside;
    if (isPieceListHeadingLine(segment)) {
      inside = true;
      midItem = unterminated;
      continue;
    }
    if (inside && (continuation || !segment.trim() || isInventoryItemSegment(segment))) {
      midItem = unterminated;
      continue;
    }
    inside = false;
    midItem = false;
    kept += segment + separator;
  }
  return { markdown: kept, state: { insideInventory: inside, midItem } };
}

/**
 * Retire d'un acte de droit public toute liste de pièces : son titre (« INVENTAIRE DES
 * PIÈCES », « BORDEREAU DE PIÈCES », « PIÈCES ANNEXÉES »…), ses lignes, et son délimiteur. Ce
 * qui suit la liste — annexe de jurisprudence, formule finale — reste en place.
 *
 * Pourquoi (#1786, décision du 2026-09-28) : devant la juridiction administrative, l'inventaire
 * accompagne les pièces (art. R. 412-2 CJA) ; c'est un document séparé, qu'Hector produit à
 * part (« Bordereau de productions »). Dans l'écriture, la liste fait doublon et l'avocat devait
 * l'effacer à chaque acte. Les consignes des prompts l'interdisent ; ce retrait déterministe est
 * le filet pour un prompt non encore corrigé ou un modèle qui dérive.
 *
 * Le déclencheur est un TITRE de liste explicite (« INVENTAIRE / LISTE / BORDEREAU DES PIÈCES »,
 * « PIÈCES JOINTES / ANNEXÉES »), où qu'il soit dans l'acte ; le titre est retiré dès qu'il est
 * lu (le flux de l'add-in ne peut pas attendre la suite). Sont retirées ensuite les seules lignes
 * de forme pièce — « Pièce n°X… », ou numéro nu suivi d'un intitulé court sans verbe conjugué ni
 * ponctuation de phrase — et les lignes vides entre elles ; le premier paragraphe qui n'est pas une
 * pièce ferme la liste et reste intact, comme tout ce qui suit. Les renvois du corps hors liste —
 * « (pièce n°3) », « Pièce n°3 : Arrêté » sous un paragraphe — ne sont jamais touchés.
 * Idempotente : le backend l'applique au markdown persisté, l'add-in au flux, sans effet d'une
 * double passe.
 */
export function stripAdministrativeInventory(markdown: string): string {
  return stripAdministrativeInventoryFragment(markdown, INITIAL_ADMINISTRATIVE_INVENTORY_STRIP_STATE).markdown;
}

// Paragraphe qui n'est QU'UN renvoi à une de nos pièces : « Pièce n°3 : Arrêté du 12 mars »,
// « **Pièce n°3 : Arrêté** », « **Pièce n°3** : Arrêté », « Pièce n° 3 - Arrêté ». Ancré sur le
// paragraphe entier : un renvoi au fil d'une phrase n'est jamais touché. « Pièce adverse n°3 »
// et « Pièce n°3 du requérant » (productions de l'adversaire) ne correspondent pas : le
// séparateur doit suivre immédiatement le numéro.
const PIECE_REFERENCE_LINE_RE =
  /^(\s*)((?:[-*•]\s+)?)(\*\*|__)?\s*(?:pi[eè]ce|pce)\s*n\s*[°ºo]\.?\s*(\d+(?:[.-]\d+)*(?:\s?(?:bis|ter|quater)(?!\p{L}))?)\s*(\*\*|__)?\s*[:\-–—]\s*(\*\*|__)?\s*(.*?)\s*(\*\*|__)?(\s*)$/iu;

/**
 * Réécrit un paragraphe de renvoi « Pièce n°3 : Arrêté du 12 mars » en « 3 - Arrêté du 12 mars ».
 * Tout autre paragraphe est rendu tel quel. Le gras et la puce éventuels sont conservés.
 */
function rewritePieceReferenceSegment(segment: string): string {
  const m = PIECE_REFERENCE_LINE_RE.exec(segment);
  if (!m) return segment;
  const [, indent = '', bullet = '', openBold = '', number = '', midBold = '', afterSeparatorBold = '', label = '', closeBold = '', trailing = ''] = m;
  if (!label) return segment;
  const num = number.replace(/\s+/g, '');
  // « **Pièce n°3** : Arrêté » et « **Pièce n°3 :** Arrêté » : le gras ne couvre que le numéro,
  // on le referme juste après lui. Recopier le gras fermant après le tiret donnait « **3 - **
  // Arrêté », que markdown ne lit pas comme du gras (astérisques visibles, revue #1791).
  if (openBold && (midBold || afterSeparatorBold)) return `${indent}${bullet}${openBold}${num}${openBold} - ${label}${trailing}`;
  return `${indent}${bullet}${openBold}${num} - ${label}${closeBold}${trailing}`;
}

/**
 * Renvois aux pièces dans le corps d'un acte de droit public (#1786, décision du 2026-09-28) :
 * chaque pièce est citée sur un paragraphe indépendant « 3 - Délibération n° 2024-047 du 4 avril
 * 2024 », jamais « Pièce n°3 : … » — l'avocat de contentieux administratif n'écrit jamais
 * « pièce n°1 ». Réécrit les paragraphes-renvois restés à l'ancienne forme ; les renvois au fil
 * d'une phrase et les pièces de l'adversaire ne sont pas touchés.
 *
 * Sans état : chaque paragraphe se juge seul, donc la fonction s'applique aussi bien au markdown
 * persisté qu'à un fragment du flux de l'add-in. Idempotente.
 */
export function rewriteAdministrativePieceReferences(markdown: string): string {
  if (!markdown) return markdown;
  const parts = markdown.split(SEGMENT_SEPARATOR_RE);
  for (let i = 0; i < parts.length; i += 2) parts[i] = rewritePieceReferenceSegment(parts[i] ?? '');
  return parts.join('');
}
